/**
 * Compact wire format for a terrain collision mask (one byte per pixel, 0 = air, 1 = solid).
 *
 * The mask is run-length encoded as one flat sequence of alternating air / solid runs that
 * starts with air (so the first run is empty when pixel 0 is solid). Each run length is written
 * little-endian in base 32, one character per five bits; the sixth bit of a character flags that
 * another digit follows. The alphabet is URL- and JSON-safe, so the result is a plain string.
 * A 1600x900 map with a few dozen craters comes out around 10-20 KB instead of 1.44 MB.
 */
export interface EncodedTerrain {
  w: number;
  h: number;
  runs: string;
}

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';
const MORE = 32;
const DIGIT = new Int8Array(128).fill(-1);
for (let i = 0; i < ALPHABET.length; i++) DIGIT[ALPHABET.charCodeAt(i)] = i;

function pushRun(out: string[], n: number): void {
  do {
    let d = n % 32;
    n = Math.floor(n / 32);
    if (n > 0) d += MORE;
    out.push(ALPHABET[d]!);
  } while (n > 0);
}

export function encodeMask(mask: ArrayLike<number>, w: number, h: number): EncodedTerrain {
  if (!Number.isInteger(w) || !Number.isInteger(h) || w < 0 || h < 0 || mask.length !== w * h) {
    throw new Error(`encodeMask: mask has ${mask.length} entries, expected ${w}x${h}`);
  }
  const out: string[] = [];
  let value = 0;
  let run = 0;
  for (let i = 0; i < mask.length; i++) {
    const v = mask[i] ? 1 : 0;
    if (v === value) {
      run++;
      continue;
    }
    pushRun(out, run);
    value = v;
    run = 1;
  }
  pushRun(out, run);
  return { w, h, runs: out.join('') };
}

/** Decodes into an existing buffer of exactly w*h bytes. Throws on malformed input, leaving the buffer partially written. */
export function decodeMaskInto(enc: EncodedTerrain, target: Uint8Array): void {
  const total = enc.w * enc.h;
  if (target.length !== total) throw new Error(`decodeMask: buffer has ${target.length} bytes, expected ${total}`);
  const runs = enc.runs;
  let i = 0;
  let value = 0;
  let n = 0;
  let scale = 1;
  for (let k = 0; k < runs.length; k++) {
    const c = runs.charCodeAt(k);
    const d = c < 128 ? DIGIT[c]! : -1;
    if (d < 0) throw new Error(`decodeMask: bad character at ${k}`);
    n += (d & 31) * scale;
    if (d & MORE) {
      scale *= 32;
      continue;
    }
    if (i + n > total) throw new Error('decodeMask: runs exceed the mask');
    target.fill(value, i, i + n);
    i += n;
    value ^= 1;
    n = 0;
    scale = 1;
  }
  if (scale !== 1 || i !== total) throw new Error('decodeMask: truncated data');
}

export function decodeMask(enc: EncodedTerrain): Uint8Array {
  const out = new Uint8Array(enc.w * enc.h);
  decodeMaskInto(enc, out);
  return out;
}

/** Shape check for data that arrived over the wire; `decodeMask` validates the contents. */
export function isEncodedTerrain(v: unknown): v is EncodedTerrain {
  if (!v || typeof v !== 'object') return false;
  const e = v as Record<string, unknown>;
  return Number.isInteger(e.w) && Number.isInteger(e.h) && (e.w as number) >= 0 && (e.h as number) >= 0 && typeof e.runs === 'string';
}
